import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PdfPreviewModal from "../components/PdfPreviewModal";
import FlameHeart from "../components/FlameHeart";
import { useAppVersion } from "../hooks/useAppVersion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GIORNI = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];

const emptyProfilo = () =>
  GIORNI.reduce((acc, g) => ({ ...acc, [g]: { ore: 0, orario: "", motivo_perm: "", ore_assenza: 0 } }), {});

// Tiny inline icons (kept here so the file is self-contained).
const I = {
  Plus: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Trash: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    </svg>
  ),
  Edit: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Clock: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Back: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
};

/* =========================================================================
   Root module
   ========================================================================= */

const PeriodContext = React.createContext({ anno: "", mese: "", setAnno: () => {}, setMese: () => {} });

export default function GestionePresenze({ goHome, isAdmin = false }) {
  // Phase 1 nav: dipendenti | motivi
  // (tabellone, assenze wizard, straordinarie sono fasi successive)
  const [view, setView] = useState("tabellone");
  const [anno, setAnno] = useState("");
  const [mese, setMese] = useState("");
  const version = useAppVersion();

  // If a non-admin somehow lands on the "import" view (e.g. via stale state),
  // bounce them back to the default view to avoid showing the import UI.
  useEffect(() => {
    if (!isAdmin && view === "import") setView("tabellone");
  }, [isAdmin, view]);

  const periodValue = useMemo(() => ({ anno, mese, setAnno, setMese }), [anno, mese]);

  return (
    <PeriodContext.Provider value={periodValue}>
    <div className="min-h-screen bg-[var(--c-bg)] flex flex-col" data-testid="presenze-module">
      <header className="px-8 py-4 flex items-center justify-between bg-[#0a0a0a] text-white border-b border-[#0a0a0a]">
        <div className="flex items-center gap-6">
          <button
            onClick={goHome}
            className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-[var(--c-accent)] flex items-center gap-1.5"
            data-testid="presenze-go-home"
          >
            <I.Back className="w-3.5 h-3.5" /> Menu principale
          </button>
          <div className="flex items-center gap-3">
            <FlameHeart size={28} />
            <div className="font-display text-xl text-white">
              FUOCOIN <span className="text-[var(--c-accent)]">HUB</span>{" "}
              <span className="text-white">Presenze</span>
              <span className="font-mono text-xs text-white/50 ml-3" data-testid="app-version-presenze">v{version}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar */}
        <aside className="w-60 border-r border-[var(--c-text)] bg-[var(--c-text)] text-white py-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-5 mb-2">
            Operatività
          </div>
          <SideLink id="tabellone" view={view} onClick={setView} label="Tabellone Mensile" testid="nav-tabellone" />
          <SideLink id="wizard" view={view} onClick={setView} label="Wizard Assenze" testid="nav-wizard" />
          <SideLink id="straord" view={view} onClick={setView} label="Straordinarie" testid="nav-straord" />

          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-5 mt-6 mb-2">
            Anagrafiche
          </div>
          <SideLink id="dipendenti" view={view} onClick={setView} label="Dipendenti" testid="nav-dipendenti" />
          <SideLink id="motivi" view={view} onClick={setView} label="Motivi Assenza" testid="nav-motivi" />

          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-5 mt-6 mb-2">
            Amministrazione
          </div>
          {isAdmin && (
            <SideLink id="import" view={view} onClick={setView} label="Import Dati" testid="nav-import" />
          )}
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          {view === "tabellone" && <TabelloneView />}
          {view === "wizard" && <WizardAssenzeView />}
          {view === "straord" && <StraordinarieView />}
          {view === "dipendenti" && <DipendentiView />}
          {view === "motivi" && <MotiviView />}
          {isAdmin && view === "import" && <ImportView />}
        </main>
      </div>
    </div>
    </PeriodContext.Provider>
  );
}

function SideLink({ id, view, onClick, label, testid, disabled }) {
  const active = view === id;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : () => onClick(id)}
      disabled={disabled}
      className={[
        "w-full text-left px-5 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors",
        active
          ? "bg-[var(--c-accent)] text-white"
          : disabled
            ? "text-white/30 cursor-not-allowed"
            : "text-white/90 hover:bg-white/10",
      ].join(" ")}
      data-testid={testid}
    >
      {label}
      {disabled && <span className="ml-2 text-[9px] opacity-60">— FASE 2</span>}
    </button>
  );
}

/* =========================================================================
   Dipendenti view — list, create, edit (with profilo orario)
   ========================================================================= */

function DipendentiView() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // dipendente or {} for new

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dipendenti`);
      setItems(r.data);
    } catch (e) {
      toast.error("Caricamento dipendenti fallito.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (d) => {
    if (!window.confirm(`Eliminare ${d.cognome} ${d.nome}? Verranno cancellate anche le presenze associate.`)) return;
    try {
      await axios.delete(`${API}/dipendenti/${d.id}`);
      toast.success("Dipendente eliminato.");
      load();
    } catch (e) {
      toast.error("Eliminazione fallita.");
    }
  };

  return (
    <div className="p-8" data-testid="view-dipendenti">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
            ▌ Anagrafica
          </div>
          <h2 className="font-display text-4xl">Dipendenti</h2>
          <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Gestione dipendenti e profilo orario settimanale.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2"
          data-testid="dipendente-new"
        >
          <I.Plus className="w-4 h-4" /> Nuovo dipendente
        </button>
      </div>

      {editing && (
        <DipendenteForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {items === null ? (
        <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="dipendenti-empty">
          Nessun dipendente. Clicca <strong className="text-[var(--c-accent)]">NUOVO DIPENDENTE</strong> per iniziare.
        </div>
      ) : (
        <table className="w-full font-mono text-sm">
          <thead>
            <tr>
              <Th>Cognome</Th>
              <Th>Nome</Th>
              <Th>Attivo</Th>
              <Th>Ore settimana</Th>
              <Th>Giorni lavorativi</Th>
              <Th className="text-right">Azioni</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const totSett = GIORNI.reduce((s, g) => s + (d.profilo?.[g]?.ore || 0), 0);
              const giorniLav = GIORNI.filter((g) => (d.profilo?.[g]?.ore || 0) > 0);
              return (
                <tr key={d.id} className="hover:bg-[var(--c-surface)]" data-testid={`dipendente-row-${d.id}`}>
                  <Td>{d.cognome}</Td>
                  <Td>{d.nome}</Td>
                  <Td>
                    {d.attivo
                      ? <span className="inline-block px-2 py-0.5 bg-[var(--c-accent)] text-white text-[9px] uppercase tracking-widest">SI</span>
                      : <span className="inline-block px-2 py-0.5 border border-[var(--c-border-soft)] text-[var(--c-text-muted)] text-[9px] uppercase tracking-widest">NO</span>}
                  </Td>
                  <Td className="tabular-nums">{formatHHMM(totSett)}</Td>
                  <Td className="text-[var(--c-text-muted)] text-[10px] uppercase tracking-widest">
                    {giorniLav.join(" · ") || "—"}
                  </Td>
                  <Td className="text-right">
                    <button
                      onClick={() => setEditing(d)}
                      className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-border-soft)] hover:border-[var(--c-text)] mr-1.5"
                      data-testid={`dipendente-edit-${d.id}`}
                    >
                      <I.Edit className="w-3.5 h-3.5 inline" /> Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(d)}
                      className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border"
                      style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }}
                      data-testid={`dipendente-del-${d.id}`}
                    >
                      <I.Trash className="w-3.5 h-3.5 inline" />
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 border-b border-[var(--c-border-soft)] ${className}`}>{children}</td>;
}

/* =========================================================================
   Dipendente form (incl. profilo orario)
   ========================================================================= */

function DipendenteForm({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [cognome, setCognome] = useState(initial.cognome || "");
  const [nome, setNome] = useState(initial.nome || "");
  const [attivo, setAttivo] = useState(initial.attivo ?? true);
  const [profilo, setProfilo] = useState(initial.profilo || emptyProfilo());
  const [busy, setBusy] = useState(false);

  const updateProfilo = (g, key, value) => {
    setProfilo((p) => ({
      ...p,
      [g]: { ...(p[g] || { ore: 0, orario: "" }), [key]: value },
    }));
  };

  const totSett = useMemo(
    () => GIORNI.reduce((s, g) => s + (Number(profilo?.[g]?.ore) || 0), 0),
    [profilo],
  );

  const save = async () => {
    if (!cognome.trim() || !nome.trim()) {
      toast.error("Cognome e nome obbligatori.");
      return;
    }
    setBusy(true);
    try {
      // normalize profilo numbers
      const normProfilo = GIORNI.reduce((acc, g) => {
        acc[g] = {
          ore: Number(profilo?.[g]?.ore) || 0,
          orario: String(profilo?.[g]?.orario || ""),
          motivo_perm: String(profilo?.[g]?.motivo_perm || "").trim(),
          ore_assenza: Number(profilo?.[g]?.ore_assenza) || 0,
        };
        return acc;
      }, {});
      const body = {
        cognome: cognome.trim(),
        nome: nome.trim(),
        attivo: !!attivo,
        profilo: normProfilo,
      };
      if (isNew) {
        await axios.post(`${API}/dipendenti`, body);
        toast.success("Dipendente creato.");
      } else {
        await axios.put(`${API}/dipendenti/${initial.id}`, body);
        toast.success("Dipendente aggiornato.");
      }
      onSaved();
    } catch (e) {
      toast.error("Salvataggio fallito.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[var(--c-text)] p-6 mb-6 shadow-[6px_6px_0_var(--c-accent)]" data-testid="dipendente-form">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)] mb-1">
            {isNew ? "Nuovo" : "Modifica"}
          </div>
          <h3 className="font-display text-2xl">
            {isNew ? "Nuovo dipendente" : `${initial.cognome} ${initial.nome}`}
          </h3>
        </div>
        <div className="text-right font-mono text-xs text-[var(--c-text-muted)]">
          Tot. settimanale
          <div className="font-display text-2xl text-[var(--c-text)] tabular-nums">
            {formatHHMM(totSett)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Field label="Cognome" required>
          <input value={cognome} onChange={(e) => setCognome(e.target.value)} className="input-base" data-testid="df-cognome" />
        </Field>
        <Field label="Nome" required>
          <input value={nome} onChange={(e) => setNome(e.target.value)} className="input-base" data-testid="df-nome" />
        </Field>
        <Field label="Attivo">
          <label className="flex items-center gap-2 h-[42px] px-3 border border-[var(--c-border-soft)] cursor-pointer hover:border-[var(--c-text)]">
            <input type="checkbox" checked={attivo} onChange={(e) => setAttivo(e.target.checked)} data-testid="df-attivo" />
            <span className="font-mono text-xs">{attivo ? "Sì, attivo" : "Non attivo"}</span>
          </label>
        </Field>
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-3">
        ▌ Profilo orario settimanale
      </div>

      <table className="w-full font-mono text-sm mb-6">
        <thead>
          <tr>
            <Th>Giorno</Th>
            <Th className="w-24">Ore</Th>
            <Th className="w-24">Ore ass.</Th>
            <Th className="w-24">Motivo perm.</Th>
            <Th>Orario (testo libero)</Th>
          </tr>
        </thead>
        <tbody>
          {GIORNI.map((g) => (
            <tr key={g} data-testid={`profilo-row-${g}`}>
              <Td className="font-bold w-24">{g}</Td>
              <Td>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={profilo[g]?.ore ?? 0}
                  onChange={(e) => updateProfilo(g, "ore", e.target.value)}
                  className="input-base tabular-nums w-24"
                  data-testid={`profilo-ore-${g}`}
                />
              </Td>
              <Td>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={profilo[g]?.ore_assenza ?? 0}
                  onChange={(e) => updateProfilo(g, "ore_assenza", e.target.value)}
                  className="input-base tabular-nums w-24"
                  data-testid={`profilo-oreass-${g}`}
                />
              </Td>
              <Td>
                <input
                  type="text"
                  placeholder="es. L104"
                  value={profilo[g]?.motivo_perm ?? ""}
                  onChange={(e) => updateProfilo(g, "motivo_perm", e.target.value.toUpperCase())}
                  className="input-base uppercase w-24"
                  data-testid={`profilo-motivo-${g}`}
                />
              </Td>
              <Td>
                <input
                  type="text"
                  placeholder="es. 9-12.30/15-19.30"
                  value={profilo[g]?.orario ?? ""}
                  onChange={(e) => updateProfilo(g, "orario", e.target.value)}
                  className="input-base w-full"
                  data-testid={`profilo-orario-${g}`}
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest border border-[var(--c-border-soft)] hover:bg-[var(--c-surface)]" data-testid="df-cancel">
          Annulla
        </button>
        <button onClick={save} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="df-save">
          {busy ? "Salvataggio…" : isNew ? "Crea" : "Salva"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">
        {label} {required && <span className="text-[var(--c-accent)]">*</span>}
      </span>
      {children}
    </label>
  );
}

/* =========================================================================
   Motivi Assenza view
   ========================================================================= */

function MotiviView() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/motivi-assenza`);
      setItems(r.data);
    } catch (e) {
      toast.error("Caricamento motivi fallito.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (m) => {
    if (!window.confirm(`Eliminare il motivo "${m.codice}"?`)) return;
    try {
      await axios.delete(`${API}/motivi-assenza/${m.id}`);
      toast.success("Motivo eliminato.");
      load();
    } catch (e) {
      toast.error("Eliminazione fallita.");
    }
  };

  return (
    <div className="p-8" data-testid="view-motivi">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
            ▌ Configurazione
          </div>
          <h2 className="font-display text-4xl">Motivi Assenza</h2>
          <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
            Codici e descrizioni per giustificare le ore di assenza (HHA).
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 flex items-center gap-2"
          data-testid="motivo-new"
        >
          <I.Plus className="w-4 h-4" /> Nuovo motivo
        </button>
      </div>

      {editing && (
        <MotivoForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {items === null ? (
        <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="motivi-empty">
          Nessun motivo. Clicca <strong className="text-[var(--c-accent)]">NUOVO MOTIVO</strong>.
        </div>
      ) : (
        <table className="w-full font-mono text-sm max-w-3xl">
          <thead>
            <tr>
              <Th className="w-32">Codice</Th>
              <Th>Descrizione</Th>
              <Th className="text-right w-40">Azioni</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-[var(--c-surface)]" data-testid={`motivo-row-${m.id}`}>
                <Td className="font-bold uppercase">{m.codice}</Td>
                <Td>{m.descrizione}</Td>
                <Td className="text-right">
                  <button
                    onClick={() => setEditing(m)}
                    className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-border-soft)] hover:border-[var(--c-text)] mr-1.5"
                    data-testid={`motivo-edit-${m.id}`}
                  >
                    <I.Edit className="w-3.5 h-3.5 inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(m)}
                    className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border"
                    style={{ borderColor: "var(--c-accent)", color: "var(--c-accent)" }}
                    data-testid={`motivo-del-${m.id}`}
                  >
                    <I.Trash className="w-3.5 h-3.5 inline" />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MotivoForm({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [codice, setCodice] = useState(initial.codice || "");
  const [descrizione, setDescrizione] = useState(initial.descrizione || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!codice.trim()) {
      toast.error("Codice obbligatorio.");
      return;
    }
    setBusy(true);
    try {
      const body = { codice: codice.trim(), descrizione: descrizione.trim() };
      if (isNew) {
        await axios.post(`${API}/motivi-assenza`, body);
        toast.success("Motivo creato.");
      } else {
        await axios.put(`${API}/motivi-assenza/${initial.id}`, body);
        toast.success("Motivo aggiornato.");
      }
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Salvataggio fallito.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[var(--c-text)] p-5 mb-6 shadow-[6px_6px_0_var(--c-accent)] max-w-3xl" data-testid="motivo-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Field label="Codice" required>
          <input value={codice} onChange={(e) => setCodice(e.target.value)} className="input-base" data-testid="mf-codice" placeholder="es. FE" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Descrizione">
            <input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} className="input-base" data-testid="mf-descrizione" placeholder="es. FERIE" />
          </Field>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest border border-[var(--c-border-soft)] hover:bg-[var(--c-surface)]" data-testid="mf-cancel">
          Annulla
        </button>
        <button onClick={save} disabled={busy} className="px-4 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="mf-save">
          {busy ? "Salvataggio…" : isNew ? "Crea" : "Salva"}
        </button>
      </div>
    </div>
  );
}

/* =========================================================================
   Tabellone Mensile — Phase 2
   ========================================================================= */

const MESI_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function todayYM() {
  const d = new Date();
  return { anno: d.getFullYear(), mese: d.getMonth() + 1 };
}

function TabelloneView() {
  const { anno: y0 } = todayYM();
  const { anno, mese, setAnno, setMese } = React.useContext(PeriodContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [motivi, setMotivi] = useState([]);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename, tipo, label }
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("attivi"); // "attivi" | "non_attivi" | "tutti"
  // Email-from-PDF flow rimosso: l'invio email avviene ora solo dal modulo
  // Consulente del Lavoro (selezione multi-PDF + email unica).

  const load = useCallback(async () => {
    if (!anno || !mese) { setData(null); return; }
    setLoading(true);
    try {
      const [r, mr] = await Promise.all([
        axios.get(`${API}/presenze/tabellone`, { params: { anno, mese, filtro: activeFilter } }),
        axios.get(`${API}/motivi-assenza`),
      ]);
      setData(r.data);
      setMotivi(mr.data || []);
    } catch (e) {
      toast.error("Caricamento tabellone fallito.");
    } finally {
      setLoading(false);
    }
  }, [anno, mese, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const setFestivo = async (giorno, valore) => {
    try {
      const r = await axios.put(`${API}/presenze/festivi/${anno}/${mese}`, {
        giorno,
        valore,
      });
      setData((d) => d ? { ...d, festivi: r.data.festivi } : d);
    } catch (e) {
      toast.error("Errore salvataggio festivo.");
    }
  };

  const onDipendenteSaved = (dipId, newDays, newAttivoMese, totals) => {
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        dipendenti: d.dipendenti.map((dp) =>
          dp.id !== dipId ? dp : {
            ...dp,
            days: newDays,
            attivo_mese: newAttivoMese ?? dp.attivo_mese,
            tot_hh: totals?.tot_hh ?? dp.tot_hh,
            tot_hhs: totals?.tot_hhs ?? dp.tot_hhs,
            tot_hha: totals?.tot_hha ?? dp.tot_hha,
            persisted: true,
          },
        ),
      };
    });
  };

  const openPdf = async (tipo, label) => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const r = await axios.get(`${API}/presenze/pdf/${tipo}/${anno}/${mese}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const filename = `${tipo}-${anno}-${String(mese).padStart(2, "0")}.pdf`;
      setPdfPreview({ url, filename, tipo, label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generazione PDF fallita.");
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdf = () => {
    if (pdfPreview?.url) {
      try { URL.revokeObjectURL(pdfPreview.url); } catch (_) { /* ignore */ }
    }
    setPdfPreview(null);
  };

  return (
    <div className="p-4" data-testid="view-tabellone">
      {/* Toolbar */}
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
            ▌ Operatività
          </div>
          <h2 className="font-display text-3xl">Tabellone Mensile</h2>
          <div className="inline-block mt-2 px-3 py-1.5 border-2 border-[var(--c-text)] bg-yellow-100 font-mono text-[11px] uppercase tracking-widest text-[var(--c-text)]" data-testid="tab-hhmm-notice">
            Valori espressi in <strong>HH:MM</strong>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="font-mono text-sm font-bold uppercase tracking-widest text-[var(--c-text)] bg-white px-2 py-1.5">Anno</label>
          <select
            value={anno}
            onChange={(e) => setAnno(e.target.value ? parseInt(e.target.value, 10) : "")}
            className="bg-[var(--c-text)] text-white font-mono text-sm font-bold px-3 py-1.5 border-2 border-[var(--c-text)] focus:outline-none focus:border-[var(--c-accent)] cursor-pointer"
            data-testid="tab-anno"
          >
            <option value="">—</option>
            {Array.from({ length: 11 }, (_, i) => y0 - 5 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <label className="font-mono text-sm font-bold uppercase tracking-widest text-[var(--c-text)] bg-white px-2 py-1.5">Mese</label>
          <select
            value={mese}
            onChange={(e) => setMese(e.target.value ? parseInt(e.target.value, 10) : "")}
            className="bg-[var(--c-text)] text-white font-mono text-sm font-bold px-3 py-1.5 border-2 border-[var(--c-text)] focus:outline-none focus:border-[var(--c-accent)] cursor-pointer"
            data-testid="tab-mese"
          >
            <option value="">—</option>
            {MESI_IT.map((m, idx) => (
              <option key={idx} value={idx + 1}>{m}</option>
            ))}
          </select>
          <div className="inline-flex border-2 border-[var(--c-text)] ml-1" data-testid="tab-filtro-attivi">
            {[
              { id: "attivi", label: "Attivi" },
              { id: "non_attivi", label: "Non attivi" },
              { id: "tutti", label: "Tutti" },
            ].map((opt, i) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setActiveFilter(opt.id)}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${i > 0 ? "border-l-2 border-[var(--c-text)]" : ""} ${activeFilter === opt.id ? "bg-[var(--c-text)] text-white" : "bg-white text-[var(--c-text)] hover:bg-[var(--c-surface)]"}`}
                data-testid={`tab-filtro-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Print actions row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mr-2">
          ▌ Stampa
        </div>
        <button
          onClick={() => openPdf("presenze", "Presenze")}
          disabled={pdfLoading}
          className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 border-[var(--c-text)] bg-white hover:bg-[var(--c-text)] hover:text-white transition-colors disabled:opacity-40"
          data-testid="pdf-open-presenze"
        >
          PDF Presenze
        </button>
        <button
          onClick={() => openPdf("assenze", "Assenze")}
          disabled={pdfLoading}
          className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 border-[var(--c-text)] bg-white hover:bg-[var(--c-text)] hover:text-white transition-colors disabled:opacity-40"
          data-testid="pdf-open-assenze"
        >
          PDF Assenze
        </button>
        <button
          onClick={() => openPdf("straordinarie", "Straordinarie")}
          disabled={pdfLoading}
          className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 border-[var(--c-text)] bg-white hover:bg-[var(--c-text)] hover:text-white transition-colors disabled:opacity-40"
          data-testid="pdf-open-straordinarie"
        >
          PDF Straordinarie
        </button>
        {pdfLoading && <span className="font-mono text-[10px] text-[var(--c-accent)] uppercase tracking-widest animate-pulse">Generazione…</span>}
      </div>

      {loading && (
        <div className="font-mono text-xs text-[var(--c-text-muted)]" data-testid="tab-loading">
          Caricamento…
        </div>
      )}

      {!loading && (!anno || !mese) && (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="tab-select-prompt">
          Seleziona <strong>Anno</strong> e <strong>Mese</strong> per visualizzare il tabellone.
        </div>
      )}

      {!loading && anno && mese && data && data.dipendenti.length === 0 && (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="tab-empty">
          Nessun dipendente in anagrafica. Aggiungine almeno uno da <strong>Dipendenti</strong>.
        </div>
      )}

      {!loading && anno && mese && data && data.dipendenti.length > 0 && (
        <div className="border border-[var(--c-text)] bg-white overflow-auto max-h-[calc(100vh-220px)]" data-testid="tab-grid-scroll">
          <TabelloneGrid
            data={data}
            motivi={motivi}
            anno={anno}
            mese={mese}
            onFestivoToggle={setFestivo}
            onDipendenteSaved={onDipendenteSaved}
            onReload={load}
          />
        </div>
      )}

      {pdfPreview && (
        <PdfPreviewModal
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          title={`${pdfPreview.label} — ${MESI_IT[mese - 1]} ${anno}`}
          onClose={closePdf}
        />
      )}
    </div>
  );
}

/* ----- The grid ----- */
function TabelloneGrid({ data, motivi, anno, mese, onFestivoToggle, onDipendenteSaved, onReload }) {
  const giorni = Array.from({ length: data.giorni_count }, (_, i) => i + 1);

  return (
    <table className="font-mono text-[11px] border-collapse w-full" style={{ tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "140px" }} />
        <col style={{ width: "34px" }} />
        {giorni.map((g) => <col key={g} style={{ width: "auto" }} />)}
        <col style={{ width: "56px" }} />
      </colgroup>
      <thead>
        <tr className="bg-[var(--c-text)] text-white">
          <th className="sticky left-0 top-0 z-30 bg-[var(--c-text)] text-white p-2 text-left border-r-2 border-white/30 text-[10px] uppercase tracking-widest">
            Dipendente
          </th>
          <th className="sticky top-0 z-20 bg-[var(--c-text)] p-1 text-center text-[9px] uppercase tracking-widest border-r-2 border-white/30">
            Riga
          </th>
          {giorni.map((g) => (
            <th key={g} className="sticky top-0 z-20 bg-[var(--c-text)] p-1 text-center border-l border-white/10 tabular-nums text-[13px] font-display">
              {g}
            </th>
          ))}
          <th className="sticky top-0 z-20 bg-[var(--c-text)] p-2 text-right border-l-2 border-white/30 text-[10px] uppercase tracking-widest">TOT</th>
        </tr>
        <tr className="bg-[#1a1a1a] text-white">
          <th className="sticky left-0 top-[30px] z-30 bg-[#1a1a1a] text-white p-1 text-left text-[9px] uppercase tracking-widest border-r-2 border-white/30">
            Giorno sett.
          </th>
          <th className="sticky top-[30px] z-20 bg-[#1a1a1a] border-r-2 border-white/30"></th>
          {giorni.map((g) => {
            const dow = (data.giorni_settimana || {})[g] || "";
            const isDom = dow === "DOM";
            const isFest = !!(data.festivi || {})[g];
            const highlight = isDom || isFest;
            return (
              <th
                key={g}
                className={`sticky top-[30px] z-20 bg-[#1a1a1a] p-1 text-center border-l border-white/10 text-[9px] uppercase tracking-widest font-bold ${highlight ? "text-[var(--c-accent)]" : ""}`}
                data-testid={`tab-dow-${g}`}
              >
                {dow}
              </th>
            );
          })}
          <th className="sticky top-[30px] z-20 bg-[#1a1a1a] p-1 border-l-2 border-white/30"></th>
        </tr>
        <tr className="bg-yellow-100 border-b-2 border-[var(--c-text)]">
          <th className="sticky left-0 top-[52px] z-30 bg-yellow-100 p-1 text-left text-[9px] uppercase tracking-widest text-[var(--c-text)] border-r-2 border-[var(--c-text)] font-bold">
            Festivo
          </th>
          <th className="sticky top-[52px] z-20 bg-yellow-100 border-r-2 border-[var(--c-text)]"></th>
          {giorni.map((g) => (
            <td key={g} className="sticky top-[52px] z-20 bg-yellow-100 p-0.5 text-center border-l border-yellow-300" data-testid={`tab-fest-${g}`}>
              <input
                type="checkbox"
                checked={!!(data.festivi || {})[g]}
                onChange={(e) => onFestivoToggle(g, e.target.checked)}
                className="cursor-pointer w-3.5 h-3.5 accent-[var(--c-accent)]"
                data-testid={`tab-fest-chk-${g}`}
              />
            </td>
          ))}
          <td className="sticky top-[52px] z-20 bg-yellow-100 border-l-2 border-[var(--c-text)]"></td>
        </tr>
      </thead>
      <tbody>
        {data.dipendenti.map((dp) => (
          <DipendenteBlock
            key={dp.id}
            dipendente={dp}
            giorni={giorni}
            festivi={data.festivi || {}}
            giorniSettimana={data.giorni_settimana || {}}
            motivi={motivi}
            anno={anno}
            mese={mese}
            onSaved={onDipendenteSaved}
            onReload={onReload}
          />
        ))}
      </tbody>
    </table>
  );
}

/* ----- Per-dipendente block: 5 rows (HH/HHS/HHA/MOTIVO/BLOCCATO) ----- */
function DipendenteBlock({ dipendente, giorni, festivi, giorniSettimana, motivi, anno, mese, onSaved, onReload }) {
  // Local working state — we keep a ref to the latest days object so blur
  // handlers don't need a re-render to commit.
  const [days, setDays] = useState(() => dipendente.days || {});
  const [attivoMese, setAttivoMese] = useState(!!dipendente.attivo_mese);
  const [totHH, setTotHH] = useState(dipendente.tot_hh);
  const [totHHS, setTotHHS] = useState(dipendente.tot_hhs);
  const [totHHA, setTotHHA] = useState(dipendente.tot_hha);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  // re-sync when parent data changes (e.g. month switch)
  useEffect(() => {
    setDays(dipendente.days || {});
    setAttivoMese(!!dipendente.attivo_mese);
    setTotHH(dipendente.tot_hh);
    setTotHHS(dipendente.tot_hhs);
    setTotHHA(dipendente.tot_hha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dipendente.id, anno, mese]);

  const recomputeTotals = useCallback((srcDays) => {
    let h = 0, s = 0, a = 0;
    Object.values(srcDays || {}).forEach((c) => {
      h += Number(c.hh) || 0;
      s += Number(c.hhs) || 0;
      a += Number(c.hha) || 0;
    });
    setTotHH(Number(h.toFixed(2)));
    setTotHHS(Number(s.toFixed(2)));
    setTotHHA(Number(a.toFixed(2)));
    return { tot_hh: Number(h.toFixed(2)), tot_hhs: Number(s.toFixed(2)), tot_hha: Number(a.toFixed(2)) };
  }, []);

  const scheduleSave = useCallback((newDays, newAttivoMese) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await axios.put(
          `${API}/presenze/${dipendente.id}/${anno}/${mese}`,
          { attivo_mese: newAttivoMese, note: "", days: newDays },
        );
        onSaved(dipendente.id, newDays, newAttivoMese, r.data);
      } catch (e) {
        toast.error(`Salvataggio fallito per ${dipendente.cognome}.`);
      } finally {
        setSaving(false);
      }
    }, 350);
  }, [dipendente.id, dipendente.cognome, anno, mese, onSaved]);

  const updateCell = useCallback((giorno, field, raw) => {
    setDays((prev) => {
      const g = String(giorno);
      const cell = { ...(prev[g] || { hh: 0, hhs: 0, hha: 0, motivo: "", bloccato: false }) };
      if (field === "motivo") {
        cell.motivo = String(raw || "");
      } else if (field === "bloccato") {
        cell.bloccato = !!raw;
      } else if (field === "hha") {
        // Assenza scala dalle ore lavorate: se HH=6 e si imputa HHA=2, HH diventa 4.
        // Quando HHA scende, HH risale di pari quantità (con clamp a 0).
        const newHHA = typeof raw === "number" ? raw : parseHours(raw);
        const oldHHA = Number(cell.hha) || 0;
        const delta = newHHA - oldHHA;
        const oldHH = Number(cell.hh) || 0;
        cell.hha = newHHA;
        cell.hh = Math.max(0, oldHH - delta);
      } else {
        const n = typeof raw === "number" ? raw : parseHours(raw);
        cell[field] = n;
      }
      const next = { ...prev, [g]: cell };
      recomputeTotals(next);
      scheduleSave(next, attivoMese);
      return next;
    });
  }, [recomputeTotals, scheduleSave, attivoMese]);

  const toggleAttivoMese = (v) => {
    setAttivoMese(v);
    scheduleSave(days, v);
  };

  const resetSchema = async () => {
    if (!window.confirm(`Reset schema presenze per ${dipendente.cognome} ${dipendente.nome}?\nI giorni "Bloccato" resteranno invariati.`)) return;
    try {
      const r = await axios.post(`${API}/presenze/${dipendente.id}/${anno}/${mese}/reset`);
      setDays(r.data.days || {});
      recomputeTotals(r.data.days || {});
      toast.success("Schema presenze ripristinato dal profilo.");
      onReload && onReload();
    } catch (e) {
      toast.error("Reset fallito.");
    }
  };

  const fullName = `${dipendente.cognome} ${dipendente.nome}`;

  return (
    <>
      {/* Heavy divider between dipendenti */}
      <tr className="h-1.5 bg-[var(--c-text)]">
        <td className="sticky left-0 z-10 bg-[var(--c-text)]"></td>
        <td className="bg-[var(--c-text)]"></td>
        {giorni.map((g) => <td key={g} className="bg-[var(--c-text)]"></td>)}
        <td className="bg-[var(--c-text)]"></td>
      </tr>

      {/* HH row */}
      <tr className="bg-white" data-testid={`tab-row-${dipendente.id}`}>
        <td
          rowSpan={5}
          className="sticky left-0 z-10 bg-white border-r-2 border-[var(--c-text)] p-2 align-top shadow-[inset_-4px_0_8px_-6px_rgba(0,0,0,0.25)]"
        >
          <div className="font-display text-[13px] leading-tight" data-testid={`tab-dip-name-${dipendente.id}`}>
            {fullName}
          </div>
          <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={attivoMese}
              onChange={(e) => toggleAttivoMese(e.target.checked)}
              className="w-3.5 h-3.5 accent-[var(--c-accent)]"
              data-testid={`tab-attivo-mese-${dipendente.id}`}
            />
            <span className="text-[9px] uppercase tracking-widest font-bold leading-tight">Attivo nel mese</span>
          </label>
          <button
            onClick={resetSchema}
            className="mt-2 px-2 py-1 font-mono text-[9px] uppercase tracking-widest border-2 border-[var(--c-text)] hover:bg-[var(--c-text)] hover:text-white w-full text-left transition-colors"
            data-testid={`tab-reset-${dipendente.id}`}
          >
            ↻ Reset schema
          </button>
          {saving && (
            <div className="mt-2 text-[9px] uppercase tracking-widest text-[var(--c-accent)] font-bold animate-pulse" data-testid={`tab-saving-${dipendente.id}`}>
              ● Salvataggio…
            </div>
          )}
        </td>
        <RowTag label="HH" tone="hh" />
        {giorni.map((g) => {
          const isFest = !!festivi[g];
          const dow = giorniSettimana[g];
          const isDom = dow === "DOM";
          const highlight = isFest || isDom;
          const cell = days[String(g)] || {};
          return (
            <NumCell
              key={g}
              testid={`tab-hh-${dipendente.id}-${g}`}
              value={cell.hh}
              dim={highlight}
              accentBg={highlight ? "bg-red-200" : ""}
              onCommit={(v) => updateCell(g, "hh", v)}
            />
          );
        })}
        <td className="pl-2 pr-3 py-1 text-right font-display text-[13px] tabular-nums border-l-2 border-[var(--c-text)] bg-[var(--c-text)] text-white" data-testid={`tab-tot-hh-${dipendente.id}`}>
          {formatHHMM(totHH)}
        </td>
      </tr>

      {/* HHS row */}
      <tr className="bg-blue-50/60">
        <RowTag label="HHS" tone="hhs" />
        {giorni.map((g) => {
          const cell = days[String(g)] || {};
          const hasHHS = (Number(cell.hhs) || 0) > 0;
          return (
            <NumCell
              key={g}
              testid={`tab-hhs-${dipendente.id}-${g}`}
              value={cell.hhs}
              dim={!hasHHS}
              accentBg={hasHHS ? "bg-blue-200" : ""}
              onCommit={(v) => updateCell(g, "hhs", v)}
            />
          );
        })}
        <td className="px-2 py-1 text-right tabular-nums border-l-2 border-[var(--c-text)] bg-blue-200 text-[var(--c-text)] font-bold text-[12px]" data-testid={`tab-tot-hhs-${dipendente.id}`}>
          {formatHHMM(totHHS)}
        </td>
      </tr>

      {/* HHA row */}
      <tr className="bg-red-50/70">
        <RowTag label="HHA" tone="hha" />
        {giorni.map((g) => {
          const cell = days[String(g)] || {};
          const hasAssenza = (Number(cell.hha) || 0) > 0;
          return (
            <NumCell
              key={g}
              testid={`tab-hha-${dipendente.id}-${g}`}
              value={cell.hha}
              dim={!hasAssenza}
              accentBg={hasAssenza ? "bg-red-200" : ""}
              onCommit={(v) => updateCell(g, "hha", v)}
            />
          );
        })}
        <td className="px-2 py-1 text-right tabular-nums border-l-2 border-[var(--c-text)] bg-red-200 text-[var(--c-text)] font-bold text-[12px]" data-testid={`tab-tot-hha-${dipendente.id}`}>
          {formatHHMM(totHHA)}
        </td>
      </tr>

      {/* MOTIVO row */}
      <tr className="bg-[var(--c-surface)]">
        <RowTag label="MOT" />
        {giorni.map((g) => {
          const cell = days[String(g)] || {};
          return (
            <MotivoCell
              key={g}
              testid={`tab-motivo-${dipendente.id}-${g}`}
              value={cell.motivo || ""}
              motivi={motivi}
              onCommit={(v) => updateCell(g, "motivo", v)}
            />
          );
        })}
        <td className="border-l-2 border-[var(--c-text)] bg-[var(--c-surface)]"></td>
      </tr>

      {/* BLOCCATO row */}
      <tr className="bg-zinc-100">
        <RowTag label="BLK" />
        {giorni.map((g) => {
          const cell = days[String(g)] || {};
          return (
            <td key={g} className={`p-1 text-center border-l border-zinc-300 ${cell.bloccato ? "bg-amber-200" : ""}`}>
              <input
                type="checkbox"
                checked={!!cell.bloccato}
                onChange={(e) => updateCell(g, "bloccato", e.target.checked)}
                className="cursor-pointer w-4 h-4 accent-[var(--c-text)]"
                data-testid={`tab-blocc-${dipendente.id}-${g}`}
              />
            </td>
          );
        })}
        <td className="border-l-2 border-[var(--c-text)] bg-zinc-100"></td>
      </tr>
    </>
  );
}

function RowTag({ label, tone }) {
  const colors = {
    hh: "bg-[var(--c-text)] text-white",
    hhs: "bg-blue-700 text-white",
    hha: "bg-[var(--c-accent)] text-white",
  };
  const cls = colors[tone] || "bg-zinc-200 text-[var(--c-text)]";
  return (
    <td
      className={`p-0.5 text-center text-[10px] uppercase tracking-widest font-bold border-r-2 border-[var(--c-text)] ${cls}`}
    >
      {label}
    </td>
  );
}

/* ----- Number cell with internal state (commit on blur/Enter) ----- */
const NumCell = React.memo(function NumCell({ value, onCommit, testid, dim, accentBg }) {
  const [local, setLocal] = useState(formatNum(value));

  useEffect(() => { setLocal(formatNum(value)); }, [value]);

  const commit = () => {
    const n = parseHours(local);
    const prev = Number(value) || 0;
    if (Math.abs(n - prev) > 1e-9) {
      onCommit(n);
    }
    setLocal(formatNum(n));
  };

  return (
    <td className={`p-0 border-l border-zinc-300 ${accentBg || ""}`}>
      <input
        type="text"
        inputMode="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.target.blur(); }
        }}
        className={`w-full h-8 text-center bg-transparent tabular-nums outline-none focus:bg-yellow-200 focus:font-bold ${dim ? "text-[var(--c-text-muted)] text-[10px]" : "text-[12px] font-bold"}`}
        data-testid={testid}
      />
    </td>
  );
});

function formatNum(n) {
  const v = Number(n) || 0;
  if (v === 0) return "";
  return formatHHMM(v);
}

function formatHHMM(n) {
  const v = Math.max(0, Number(n) || 0);
  const totalMin = Math.round(v * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function parseHours(s) {
  if (s == null) return 0;
  const str = String(s).trim();
  if (!str) return 0;
  const m = str.match(/^(\d+):([0-5]?\d)$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    return h + mm / 60;
  }
  const n = parseFloat(str.replace(",", "."));
  return isNaN(n) ? 0 : n;
}

/* ----- Motivo cell (datalist-driven, commit on blur) ----- */
const MotivoCell = React.memo(function MotivoCell({ value, motivi, onCommit, testid }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);

  const commit = () => {
    const v = (local || "").trim();
    if (v !== (value || "")) onCommit(v);
    setLocal(v);
  };

  return (
    <td className="p-0 border-l border-zinc-300">
      <input
        type="text"
        list="motivi-list"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className={`w-full h-8 text-center bg-transparent uppercase tabular-nums outline-none text-[10px] font-bold focus:bg-yellow-200 ${local ? "text-[var(--c-accent)]" : "text-[var(--c-text-muted)]"}`}
        data-testid={testid}
        placeholder=""
      />
      <datalist id="motivi-list">
        {motivi.map((m) => <option key={m.id} value={m.codice}>{m.descrizione}</option>)}
      </datalist>
    </td>
  );
});


/* =========================================================================
   Wizard Assenze — Phase 3
   ========================================================================= */
function WizardAssenzeView() {
  const { anno: y0, mese: m0 } = todayYM();
  const { anno: ctxAnno, mese: ctxMese, setAnno: setCtxAnno, setMese: setCtxMese } = React.useContext(PeriodContext);
  const [dipendenti, setDipendenti] = useState([]);
  const [motivi, setMotivi] = useState([]);
  const [dipId, setDipId] = useState("");
  const anno = ctxAnno || y0;
  const mese = ctxMese || m0;
  const setAnno = setCtxAnno;
  const setMese = setCtxMese;
  const [dal, setDal] = useState(1);
  const [al, setAl] = useState(1);
  const [motivo, setMotivo] = useState("FE");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/dipendenti`),
          axios.get(`${API}/motivi-assenza`),
        ]);
        setDipendenti(r1.data || []);
        setMotivi(r2.data || []);
        if (r1.data && r1.data.length && !dipId) setDipId(r1.data[0].id);
      } catch (e) {
        toast.error("Caricamento anagrafiche fallito.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dimMonth = useMemo(() => {
    const d = new Date(anno, mese, 0);
    return d.getDate();
  }, [anno, mese]);

  const submit = async () => {
    if (!dipId) { toast.error("Seleziona un dipendente."); return; }
    if (!motivo.trim()) { toast.error("Seleziona un motivo."); return; }
    const _dal = Math.max(1, Math.min(dimMonth, parseInt(dal, 10) || 1));
    const _al = Math.max(1, Math.min(dimMonth, parseInt(al, 10) || 1));
    if (_dal > _al) { toast.error('Il campo "dal" deve essere ≤ "al".'); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/presenze/${dipId}/${anno}/${mese}/assenza-bulk`,
        { dal: _dal, al: _al, motivo: motivo.trim() },
      );
      setLastResult(r.data);
      toast.success(`Applicate ${r.data.applied} assenze (skip riposo: ${r.data.skipped_riposo}, skip bloccati: ${r.data.skipped_bloccato}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inserimento fallito.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="view-wizard">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Operatività
        </div>
        <h2 className="font-display text-4xl">Wizard Assenze</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Compila più giorni di assenza in un colpo. HHA viene impostato dalle ore previste dal profilo,
          HH azzerato. Saltati: giorni "Bloccato" e giorni di riposo.
        </p>
      </div>

      <div className="bg-white border border-[var(--c-text)] p-5 shadow-[6px_6px_0_var(--c-accent)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Field label="Dipendente" required>
            <select value={dipId} onChange={(e) => setDipId(e.target.value)} className="input-base" data-testid="wiz-dip">
              <option value="">— seleziona —</option>
              {dipendenti.map((d) => (
                <option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>
              ))}
            </select>
          </Field>
          <Field label="Motivo" required>
            <select value={motivo} onChange={(e) => setMotivo(e.target.value)} className="input-base" data-testid="wiz-motivo">
              {motivi.map((m) => (
                <option key={m.id} value={m.codice}>{m.codice} — {m.descrizione}</option>
              ))}
            </select>
          </Field>
          <Field label="Anno" required>
            <select value={anno} onChange={(e) => setAnno(parseInt(e.target.value, 10))} className="input-base" data-testid="wiz-anno">
              {Array.from({ length: 11 }, (_, i) => y0 - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>
          <Field label="Mese" required>
            <select value={mese} onChange={(e) => setMese(parseInt(e.target.value, 10))} className="input-base" data-testid="wiz-mese">
              {MESI_IT.map((m, idx) => (
                <option key={idx} value={idx + 1}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label={`Dal giorno (1-${dimMonth})`} required>
            <input type="number" min={1} max={dimMonth} value={dal} onChange={(e) => setDal(e.target.value)} className="input-base tabular-nums" data-testid="wiz-dal" />
          </Field>
          <Field label={`Al giorno (1-${dimMonth})`} required>
            <input type="number" min={1} max={dimMonth} value={al} onChange={(e) => setAl(e.target.value)} className="input-base tabular-nums" data-testid="wiz-al" />
          </Field>
        </div>
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} className="px-5 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40" data-testid="wiz-confirm">
            {busy ? "Elaborazione…" : "Conferma"}
          </button>
        </div>
      </div>

      {lastResult && (
        <div className="mt-6 bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-4 font-mono text-xs" data-testid="wiz-result">
          <div className="uppercase tracking-widest text-[10px] text-[var(--c-text-muted)] mb-2">Esito</div>
          <div>Giorni applicati: <strong>{lastResult.applied}</strong></div>
          <div>Saltati (riposo profilo): {lastResult.skipped_riposo}</div>
          <div>Saltati (bloccato): {lastResult.skipped_bloccato}</div>
          <div className="mt-2 text-[var(--c-text-muted)]">
            Tot HH: {formatHHMM(lastResult.tot_hh)} · Tot HHA: {formatHHMM(lastResult.tot_hha)} · Tot HHS: {formatHHMM(lastResult.tot_hhs)}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Straordinarie — Phase 3
   ========================================================================= */
function StraordinarieView() {
  const [dipendenti, setDipendenti] = useState([]);
  const [dipId, setDipId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/dipendenti`);
        setDipendenti(r.data || []);
        if (r.data && r.data.length && !dipId) setDipId(r.data[0].id);
      } catch (e) {
        toast.error("Caricamento dipendenti fallito.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!dipId) { setData(null); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/presenze/straordinarie/${dipId}`);
      setData(r.data);
    } catch (e) {
      toast.error("Caricamento straordinarie fallito.");
    } finally {
      setLoading(false);
    }
  }, [dipId]);

  useEffect(() => { load(); }, [load]);

  const updateHHS = async (anno, mese, giorno, value) => {
    try {
      await axios.put(
        `${API}/presenze/straordinarie/${dipId}/${anno}/${mese}/${giorno}`,
        { hhs: value },
      );
      load();
    } catch (e) {
      toast.error("Salvataggio HHS fallito.");
    }
  };

  const giorni = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="p-8" data-testid="view-straord">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Operatività
        </div>
        <h2 className="font-display text-4xl">Straordinarie</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Riepilogo delle ore straordinarie per dipendente, divise per mese.
          Le modifiche si riflettono anche nel tabellone mensile.
        </p>
      </div>

      <div className="flex items-stretch gap-3 mb-6 flex-wrap">
        <div className="border-2 border-[var(--c-text)] px-4 py-2.5 bg-white">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--c-text-muted)] mb-0.5">Dipendente</div>
          <select
            value={dipId}
            onChange={(e) => setDipId(e.target.value)}
            className="font-mono text-sm bg-transparent outline-none min-w-[220px]"
            data-testid="straord-dip"
          >
            <option value="">— seleziona —</option>
            {dipendenti.map((d) => (
              <option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>
            ))}
          </select>
        </div>

        {!loading && data && (
          <div className="border-2 border-[var(--c-accent)] px-4 py-2.5 bg-[var(--c-accent)] text-white" data-testid="straord-tot-box">
            <div className="font-mono text-[9px] uppercase tracking-widest opacity-80 mb-0.5">Totale straordinarie</div>
            <div className="font-display text-xl tabular-nums leading-tight" data-testid="straord-totale">
              {formatHHMM(data.totale || 0)}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="font-mono text-xs text-[var(--c-text-muted)]" data-testid="straord-loading">Caricamento…</div>
      )}

      {!loading && data && (
        <>

          {data.mesi.length === 0 ? (
            <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="straord-empty">
              Nessuna ora straordinaria registrata per questo dipendente.
              Aggiungile dal <strong>Tabellone Mensile</strong> nella riga HHS.
            </div>
          ) : (
            <div className="border border-[var(--c-text)] bg-white overflow-x-auto" data-testid="straord-grid">
              <table className="font-mono text-xs border-collapse" style={{ minWidth: "100%" }}>
                <thead>
                  <tr className="bg-[var(--c-text)] text-white">
                    <th className="p-2 text-left min-w-[140px] border-r border-white/20">Anno / Mese</th>
                    {giorni.map((g) => (
                      <th key={g} className="p-1 text-center w-9 border-l border-white/10 tabular-nums">{g}</th>
                    ))}
                    <th className="p-2 text-right min-w-[70px] border-l border-white/20">TOT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mesi.map((row) => (
                    <tr key={`${row.anno}-${row.mese}`} className="bg-white" data-testid={`straord-row-${row.anno}-${row.mese}`}>
                      <td className="p-2 border-r border-[var(--c-border-soft)] border-b border-[var(--c-border-soft)] font-bold">
                        {MESI_IT[row.mese - 1]}<br />
                        <span className="text-[var(--c-text-muted)] text-[10px]">{row.anno}</span>
                      </td>
                      {giorni.map((g) => {
                        const val = row.days[String(g)] || 0;
                        return (
                          <StraordCell
                            key={g}
                            value={val}
                            testid={`straord-cell-${row.anno}-${row.mese}-${g}`}
                            onCommit={(v) => updateHHS(row.anno, row.mese, g, v)}
                          />
                        );
                      })}
                      <td className="p-2 text-right tabular-nums font-bold border-l border-[var(--c-text)] bg-[var(--c-surface)] border-b border-[var(--c-border-soft)]" data-testid={`straord-tot-${row.anno}-${row.mese}`}>
                        {formatHHMM(row.tot || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const StraordCell = React.memo(function StraordCell({ value, onCommit, testid }) {
  const [local, setLocal] = useState(formatNum(value));
  useEffect(() => { setLocal(formatNum(value)); }, [value]);
  const commit = () => {
    const n = parseHours(local);
    const prev = Number(value) || 0;
    if (Math.abs(n - prev) > 1e-9) onCommit(n);
    setLocal(formatNum(n));
  };
  return (
    <td className={`p-0 border-l border-[var(--c-border-soft)] border-b border-[var(--c-border-soft)] ${value > 0 ? "bg-yellow-50" : ""}`}>
      <input
        type="text"
        inputMode="decimal"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        className={`w-9 h-7 text-center bg-transparent tabular-nums outline-none focus:bg-yellow-200 focus:font-bold ${value > 0 ? "text-[var(--c-accent)] font-bold text-[11px]" : "text-[var(--c-text-muted)] text-[10px]"}`}
        data-testid={testid}
      />
    </td>
  );
});


/* =========================================================================
   Email Settings — Phase 4
   ========================================================================= */
export function EmailSettingsView() {
  const [sender, setSender] = useState("");
  const [senderName, setSenderName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [destinatario, setDestinatario] = useState("");
  const [subjectTpl, setSubjectTpl] = useState("");
  const [bodyTpl, setBodyTpl] = useState("");
  const [aziendaName, setAziendaName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMonth, setPreviewMonth] = useState(new Date().getMonth() + 1);
  const [previewYear, setPreviewYear] = useState(new Date().getFullYear());
  const [previewLabel, setPreviewLabel] = useState("Presenze");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, az] = await Promise.all([
          axios.get(`${API}/email-settings`),
          axios.get(`${API}/dati-azienda`).catch(() => ({ data: {} })),
        ]);
        setSender(s.data?.sender_email || "");
        setSenderName(s.data?.sender_name || "");
        setReplyTo(s.data?.reply_to || "");
        setDestinatario(s.data?.destinatario_fisso || "");
        setSubjectTpl(s.data?.subject_template || "");
        setBodyTpl(s.data?.body_template || "");
        setAziendaName(az.data?.ragione_sociale || "");
      } catch (e) {
        toast.error("Caricamento impostazioni fallito.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-settings`, {
        sender_email: sender.trim(),
        sender_name: senderName.trim(),
        reply_to: replyTo.trim(),
        destinatario_fisso: destinatario.trim(),
        subject_template: subjectTpl,
        body_template: bodyTpl,
      });
      toast.success("Impostazioni salvate.");
    } catch (e) {
      toast.error("Salvataggio fallito.");
    } finally {
      setSaving(false);
    }
  };

  // Render placeholders for the live preview
  const monthNames = [
    "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
    "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
  ];
  const renderTpl = (tpl) => {
    const ctx = {
      label: previewLabel,
      mese: monthNames[Math.max(0, Math.min(11, previewMonth - 1))],
      anno: String(previewYear),
      ragione_sociale: aziendaName || "(Ragione Sociale)",
      destinatario: destinatario || "(destinatario)",
    };
    let out = tpl || "";
    Object.entries(ctx).forEach(([k, v]) => {
      out = out.split("{" + k + "}").join(v);
    });
    return out;
  };

  const insertPlaceholder = (ph) => {
    setBodyTpl((prev) => (prev || "") + "{" + ph + "}");
  };

  const sendTestEmail = async () => {
    if (!destinatario.trim()) {
      toast.error("Imposta prima il destinatario fisso.");
      return;
    }
    setSendingTest(true);
    try {
      const r = await axios.post(`${API}/email-settings/test`, {
        subject_template: subjectTpl,
        body_template: bodyTpl,
        label: previewLabel,
        mese: previewMonth,
        anno: previewYear,
      });
      toast.success(`Email di test inviata a ${r.data?.destinatario}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Invio fallito";
      toast.error(`Test fallito: ${msg}`);
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <div className="p-8 font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>;
  }

  const subjectPreview = renderTpl(subjectTpl);
  const bodyPreview = renderTpl(bodyTpl);

  return (
    <div className="p-8 max-w-3xl" data-testid="view-email-settings">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Amministrazione
        </div>
        <h2 className="font-display text-4xl">Impostazioni Email</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Configurazione del mittente Resend, destinatario e template del messaggio per i tabelloni mensili.
        </p>
      </div>

      <div className="bg-white border border-[var(--c-text)] p-5 shadow-[6px_6px_0_var(--c-accent)] mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <Field label="Email mittente (tecnica)" required>
            <input
              type="email"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              className="input-base"
              placeholder="onboarding@resend.dev"
              data-testid="email-sender"
            />
          </Field>
          <Field label="Nome visibile del mittente">
            <input
              type="text"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              className="input-base"
              placeholder="Ariela - Fuoco IN"
              data-testid="email-sender-name"
            />
          </Field>
          <Field label="Rispondi a (Reply-To)">
            <input
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              className="input-base"
              placeholder="ariela@fuocoin.com"
              data-testid="email-reply-to"
            />
          </Field>
          <Field label="Destinatario fisso (es. ufficio paghe / consulente)" required>
            <input
              type="email"
              value={destinatario}
              onChange={(e) => setDestinatario(e.target.value)}
              className="input-base"
              placeholder="paghe@miostudio.it"
              data-testid="email-destinatario"
            />
          </Field>
        </div>
        <div className="text-[11px] font-mono text-[var(--c-text-muted)] leading-relaxed space-y-1.5">
          <div>
            <strong>Email mittente</strong>: l'indirizzo "tecnico" da cui parte la mail. Se non hai un dominio verificato su Resend, lascia{" "}
            <code className="bg-[var(--c-surface)] px-1">onboarding@resend.dev</code>.
          </div>
          <div>
            <strong>Nome visibile</strong>: il nome che il destinatario vede nella casella di posta (es. "Ariela - Fuoco IN").
          </div>
          <div>
            <strong>Rispondi a</strong>: quando il destinatario clicca "Rispondi", la risposta va a questo indirizzo (es. <code className="bg-[var(--c-surface)] px-1">ariela@fuocoin.com</code> su Office 365).
          </div>
        </div>
      </div>

      <div className="bg-white border border-[var(--c-text)] p-5 shadow-[6px_6px_0_var(--c-accent)] mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-3">
          ▌ Template Messaggio
        </div>
        <Field label="Oggetto email">
          <input
            type="text"
            value={subjectTpl}
            onChange={(e) => setSubjectTpl(e.target.value)}
            className="input-base font-mono text-sm"
            placeholder="{label} {mese} {anno} — FUOCOIN HUB"
            data-testid="email-subject-tpl"
          />
        </Field>
        <Field label="Corpo email">
          <textarea
            value={bodyTpl}
            onChange={(e) => setBodyTpl(e.target.value)}
            className="input-base font-mono text-sm leading-relaxed"
            rows={8}
            placeholder={"Buongiorno,\n\nin allegato il tabellone {label} del mese di {mese} {anno}.\n\nCordiali saluti,\n{ragione_sociale}"}
            data-testid="email-body-tpl"
          />
        </Field>
        <div className="mt-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)] mb-1">
            Placeholder disponibili — clicca per inserire
          </div>
          <div className="flex flex-wrap gap-2">
            {["label", "mese", "anno", "ragione_sociale", "destinatario"].map((ph) => (
              <button
                key={ph}
                type="button"
                onClick={() => insertPlaceholder(ph)}
                className="px-2 py-1 font-mono text-[11px] border border-[var(--c-text)] hover:bg-[var(--c-text)] hover:text-white"
                data-testid={`placeholder-${ph}`}
                title={`Inserisci {${ph}}`}
              >
                {"{" + ph + "}"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[var(--c-border-soft)] p-5 mb-6" data-testid="email-preview">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)]">
            ▌ Anteprima
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px]">
            <select
              value={previewLabel}
              onChange={(e) => setPreviewLabel(e.target.value)}
              className="input-base text-xs px-2 py-1"
              data-testid="preview-label"
            >
              <option value="Presenze">Presenze</option>
              <option value="Assenze">Assenze</option>
              <option value="Straordinarie">Straordinarie</option>
            </select>
            <select
              value={previewMonth}
              onChange={(e) => setPreviewMonth(Number(e.target.value))}
              className="input-base text-xs px-2 py-1"
              data-testid="preview-month"
            >
              {monthNames.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
            <input
              type="number"
              value={previewYear}
              onChange={(e) => setPreviewYear(Number(e.target.value) || previewYear)}
              className="input-base text-xs px-2 py-1 w-20"
              data-testid="preview-year"
            />
          </div>
        </div>
        <div className="border border-[var(--c-border-soft)] p-3 bg-[var(--c-surface)]">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
            Oggetto
          </div>
          <div className="font-mono text-sm font-bold mb-3" data-testid="preview-subject">
            {subjectPreview || <span className="text-[var(--c-text-muted)]">(vuoto)</span>}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--c-text-muted)]">
            Corpo
          </div>
          <pre
            className="font-mono text-sm whitespace-pre-wrap mt-1"
            data-testid="preview-body"
          >
            {bodyPreview || <span className="text-[var(--c-text-muted)]">(vuoto)</span>}
          </pre>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[11px] text-[var(--c-text-muted)]">
            Invia un'email di prova al destinatario fisso usando i template correnti (anche non salvati).
          </div>
          <button
            onClick={sendTestEmail}
            disabled={sendingTest || !destinatario.trim()}
            className="px-4 py-2 font-mono text-xs uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-text)] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="email-send-test"
            title={destinatario.trim() ? `Invia a ${destinatario}` : "Imposta prima il destinatario"}
          >
            {sendingTest ? "Invio…" : "Invia email di prova"}
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40"
          data-testid="email-settings-save"
        >
          {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}

/* =========================================================================
   Email Log — Phase 4
   ========================================================================= */
export function EmailLogView() {
  const [items, setItems] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/email-log`);
      setItems(r.data || []);
    } catch (e) {
      toast.error("Caricamento log fallito.");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPdf = async (it) => {
    try {
      const r = await axios.get(`${API}/email-log/${it.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfPreview({ url, filename: it.filename, title: it.subject });
    } catch (e) {
      toast.error("PDF non disponibile per questo invio.");
    }
  };

  const closePdf = () => {
    if (pdfPreview?.url) {
      try { URL.revokeObjectURL(pdfPreview.url); } catch (_) { /* ignore */ }
    }
    setPdfPreview(null);
  };

  const resend = async (it) => {
    if (resending) return;
    setResending(true);
    try {
      await axios.post(`${API}/email-log/${it.id}/resend`);
      toast.success(`Rinviato a ${it.destinatario}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rinvio fallito.");
    } finally {
      setResending(false);
    }
  };

  const removeLog = async (it) => {
    if (!window.confirm(`Eliminare il log dell'invio a ${it.destinatario}?`)) return;
    try {
      await axios.delete(`${API}/email-log/${it.id}`);
      toast.success("Log eliminato.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita.");
    }
  };

  return (
    <div className="p-8" data-testid="view-email-log">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Amministrazione
        </div>
        <h2 className="font-display text-4xl">Log Invii</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Storico delle email inviate con PDF archiviato. Clic sul PDF per riaprire, oppure rinvia con un click.
        </p>
      </div>

      {items === null ? (
        <div className="font-mono text-xs text-[var(--c-text-muted)]">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="email-log-empty">
          Nessun invio registrato. Manda il primo PDF dalla schermata <strong>Tabellone Mensile</strong>.
        </div>
      ) : (
        <table className="w-full font-mono text-sm" data-testid="email-log-table">
          <thead>
            <tr>
              <Th>Data</Th>
              <Th>Origine</Th>
              <Th>Tipo</Th>
              <Th>Periodo</Th>
              <Th>Destinatario</Th>
              <Th className="text-right">Allegati</Th>
              <Th>Stato</Th>
              <Th className="text-right">Azioni</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const d = it.created_at ? new Date(it.created_at) : null;
              const stamp = d ? d.toLocaleString("it-IT") : "";
              const origin = it.origin || "presenze";
              const originLabel = origin === "consulente" ? "CONSULENTE" : origin === "rimborsi" ? "RIMBORSI" : "PRESENZE";
              const originClass = origin === "consulente"
                ? "bg-[var(--c-accent)]/10 text-[var(--c-accent)] border-[var(--c-accent)]/40"
                : "bg-[var(--c-surface)] text-[var(--c-text-muted)] border-[var(--c-border-soft)]";
              const hasPeriod = (it.anno || 0) > 0 && (it.mese || 0) > 0;
              return (
                <tr key={it.id} className="hover:bg-[var(--c-surface)]" data-testid={`log-row-${it.id}`}>
                  <Td>{stamp}</Td>
                  <Td>
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${originClass}`} data-testid={`log-origin-${it.id}`}>{originLabel}</span>
                  </Td>
                  <Td className="uppercase">{it.tipo}</Td>
                  <Td>{hasPeriod ? `${MESI_IT[(it.mese || 1) - 1]} ${it.anno}` : "—"}</Td>
                  <Td>{it.destinatario}</Td>
                  <Td className="text-right tabular-nums">{it.attachments_count || 1}</Td>
                  <Td>
                    {it.status === "sent" ? (
                      <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] uppercase tracking-widest border border-emerald-300">Inviato</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-[9px] uppercase tracking-widest border border-red-300" title={it.error || ""}>Fallito</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openPdf(it)}
                      className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-border-soft)] hover:border-[var(--c-text)] mr-1.5"
                      data-testid={`log-pdf-${it.id}`}
                    >
                      PDF
                    </button>
                    <button
                      onClick={() => resend(it)}
                      disabled={resending}
                      className="btn-ghost px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border-2 border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-white disabled:opacity-40 transition-colors mr-1.5"
                      data-testid={`log-resend-${it.id}`}
                    >
                      Rinvia
                    </button>
                    <button
                      onClick={() => removeLog(it)}
                      className="btn-ghost px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                      data-testid={`log-delete-${it.id}`}
                      title="Elimina log"
                    >
                      ×
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pdfPreview && (
        <PdfPreviewModal
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          title={pdfPreview.title}
          onClose={closePdf}
        />
      )}
    </div>
  );
}


/* =========================================================================
   Import View — One-shot import dipendenti
   ========================================================================= */
function parseCsvLine(line) {
  // Minimal CSV row parser: handles quoted fields with "," or ";" separators.
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else cur += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',' || ch === ';' || ch === '\t') { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseDipendentiInput(text) {
  const rows = [];
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const cols = parseCsvLine(line);
    if (cols.length === 0) continue;
    if (cols.length === 1) {
      rows.push({ nome_completo: cols[0] });
    } else {
      // assume: legacy_id, nome_completo (the common case from old DB exports)
      rows.push({ legacy_id: cols[0], nome_completo: cols.slice(1).join(" ") });
    }
  }
  return rows;
}

function parseProfiloInput(text) {
  // Each row from the legacy "DIPENDENTI PROFILO" export has 11 columns; we
  // care about col 2 (giorno), col 3 (legacy_id), col 7 (motivo_perm),
  // col 8 (orario), col 9 (ore), col 10 (ore_assenza).
  const out = [];
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const cols = parseCsvLine(line);
    if (cols.length < 10) continue;
    const giorno = (cols[2] || "").trim().toUpperCase();
    const legacy_id = (cols[3] || "").trim();
    const motivo_perm = (cols[7] || "").trim();
    const orario = (cols[8] || "").trim();
    const ore_raw = (cols[9] || "").trim();
    const ore_ass_raw = (cols[10] || "").trim();
    if (!legacy_id || !giorno) continue;
    const ore = ore_raw === "" ? 0 : parseFloat(ore_raw.replace(",", ".")) || 0;
    const ore_assenza = ore_ass_raw === "" ? 0 : parseFloat(ore_ass_raw.replace(",", ".")) || 0;
    out.push({ legacy_id, giorno, ore, orario, motivo_perm, ore_assenza });
  }
  return out;
}

function parseMotiviInput(text) {
  // Each row: codice, descrizione  (2 columns).
  const out = [];
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const cols = parseCsvLine(line);
    if (cols.length === 0) continue;
    const codice = (cols[0] || "").trim();
    const descrizione = (cols[1] || "").trim();
    if (!codice) continue;
    out.push({ codice, descrizione });
  }
  return out;
}

function ImportView() {
  const [mode, setMode] = useState("dipendenti"); // "dipendenti" | "profilo" | "motivi"
  const [text, setText] = useState("");
  const [skipExisting, setSkipExisting] = useState(true);
  const [motiviMode, setMotiviMode] = useState("merge"); // "merge" | "replace"
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const previewRows = useMemo(() => {
    if (mode === "dipendenti") return parseDipendentiInput(text).slice(0, 100);
    if (mode === "profilo") return parseProfiloInput(text).slice(0, 200);
    return parseMotiviInput(text).slice(0, 100);
  }, [text, mode]);
  const totalRows = useMemo(() => {
    if (mode === "dipendenti") return parseDipendentiInput(text).length;
    if (mode === "profilo") return parseProfiloInput(text).length;
    return parseMotiviInput(text).length;
  }, [text, mode]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const content = await file.text();
      setText(content);
    } catch (err) {
      toast.error("Lettura file fallita.");
    }
  };

  const runImport = async () => {
    if (totalRows === 0) {
      toast.error("Niente da importare.");
      return;
    }
    if (!window.confirm(`Importare ${totalRows} righe (${mode})?`)) return;
    setBusy(true);
    setResult(null);
    try {
      if (mode === "dipendenti") {
        const rows = parseDipendentiInput(text);
        const r = await axios.post(`${API}/dipendenti/import-names`, {
          rows,
          skip_existing: skipExisting,
        });
        setResult({ kind: "dipendenti", ...r.data });
        toast.success(`Importati: ${r.data.created_count}, saltati: ${r.data.skipped_count}.`);
      } else if (mode === "profilo") {
        const rows = parseProfiloInput(text);
        const r = await axios.post(`${API}/dipendenti/import-profilo`, { rows });
        setResult({ kind: "profilo", ...r.data });
        toast.success(`Profili aggiornati: ${r.data.updated_count} (mancanti: ${r.data.missing_legacy_ids.length}).`);
      } else {
        const rows = parseMotiviInput(text);
        const r = await axios.post(`${API}/motivi-assenza/import`, { rows, mode: motiviMode });
        setResult({ kind: "motivi", ...r.data });
        toast.success(`Motivi: creati ${r.data.created_count}, aggiornati ${r.data.updated_count}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import fallito.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="view-import">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Amministrazione
        </div>
        <h2 className="font-display text-4xl">Import Dati</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Carica un CSV o incolla righe per importare l'anagrafica una tantum.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">Tipo:</span>
        <button
          onClick={() => { setMode("dipendenti"); setText(""); setResult(null); }}
          className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 ${mode === "dipendenti" ? "bg-[var(--c-text)] text-white border-[var(--c-text)]" : "border-[var(--c-border-soft)] hover:border-[var(--c-text)]"}`}
          data-testid="import-mode-dipendenti"
        >
          Dipendenti
        </button>
        <button
          onClick={() => { setMode("profilo"); setText(""); setResult(null); }}
          className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 ${mode === "profilo" ? "bg-[var(--c-text)] text-white border-[var(--c-text)]" : "border-[var(--c-border-soft)] hover:border-[var(--c-text)]"}`}
          data-testid="import-mode-profilo"
        >
          Profili Orari
        </button>
        <button
          onClick={() => { setMode("motivi"); setText(""); setResult(null); }}
          className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 ${mode === "motivi" ? "bg-[var(--c-text)] text-white border-[var(--c-text)]" : "border-[var(--c-border-soft)] hover:border-[var(--c-text)]"}`}
          data-testid="import-mode-motivi"
        >
          Motivi Assenza
        </button>
      </div>

      <div className="bg-white border border-[var(--c-text)] p-5 shadow-[6px_6px_0_var(--c-accent)] mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mb-2">
          Formato accettato
        </div>
        {mode === "dipendenti" ? (
          <ul className="font-mono text-xs leading-relaxed mb-4 list-disc ml-5">
            <li>Una riga per dipendente.</li>
            <li><strong>1 colonna</strong>: <code>"PASETTO ROSA"</code> → cognome = PASETTO, nome = ROSA.</li>
            <li><strong>2 colonne</strong>: <code>"28","PASETTO ROSA"</code> → la prima è l'ID storico (<code>legacy_id</code>), la seconda è il nome completo.</li>
            <li>Cognome + nome sono normalizzati in MAIUSCOLO.</li>
          </ul>
        ) : mode === "profilo" ? (
          <ul className="font-mono text-xs leading-relaxed mb-4 list-disc ml-5">
            <li>Una riga per <strong>dipendente × giorno settimana</strong> (7 righe per dipendente).</li>
            <li>Formato originale del vecchio gestionale (11 colonne):
              <code className="ml-1">uuid, autore, GIORNO, legacy_id, dt1, dt2, autore2, motivo_perm, orario, ore, ore_assenza</code>
            </li>
            <li>Match per <code>legacy_id</code>: il dipendente con quell'ID storico riceve <code>profilo[GIORNO] = {`{ore, orario, motivo_perm, ore_assenza}`}</code>.</li>
            <li>Importa prima i <strong>Dipendenti</strong> (con legacy_id) e poi i <strong>Profili Orari</strong>.</li>
            <li>Le righe con <code>legacy_id</code> non trovato vengono elencate nell'esito.</li>
          </ul>
        ) : (
          <ul className="font-mono text-xs leading-relaxed mb-4 list-disc ml-5">
            <li>Una riga per motivo: <code>codice, descrizione</code></li>
            <li>Es. <code>"FE","FERIE"</code> &middot; <code>"L104","LEGGE 104"</code></li>
            <li>Codice viene usato come chiave: se esiste già, la descrizione viene aggiornata; altrimenti viene creato.</li>
            <li>Modalità <strong>Sostituisci</strong>: la collection viene svuotata prima dell'import (usa con cautela).</li>
          </ul>
        )}

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <label className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-2 border-[var(--c-text)] cursor-pointer hover:bg-[var(--c-text)] hover:text-white inline-flex items-center gap-2 transition-colors">
            <input type="file" accept=".csv,.txt" onChange={handleFileChange} className="hidden" data-testid="import-file" />
            Carica file CSV
          </label>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">oppure incolla qui sotto:</span>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          spellCheck={false}
          className="input-base w-full font-mono text-xs"
          placeholder={mode === "dipendenti"
            ? `"28","PASETTO ROSA"\n"34","INVERNICI DAVIDE"`
            : mode === "profilo"
              ? `"uuid","Admin","LUN","28","...","...","Admin","","","8",""`
              : `"FE","FERIE"\n"M","MALATTIA"\n"L104","LEGGE 104"`}
          data-testid="import-textarea"
        />

        {previewRows.length > 0 && (
          <div className="mt-4 border border-[var(--c-border-soft)] bg-[var(--c-surface)] p-3" data-testid="import-preview">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)] mb-2">
              Anteprima ({previewRows.length} su {totalRows} righe)
            </div>
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="text-left text-[var(--c-text-muted)]">
                  <th className="py-1 pr-3">#</th>
                  {mode === "dipendenti" ? (
                    <>
                      <th className="py-1 pr-3">Legacy ID</th>
                      <th className="py-1 pr-3">Cognome</th>
                      <th className="py-1 pr-3">Nome</th>
                    </>
                  ) : mode === "profilo" ? (
                    <>
                      <th className="py-1 pr-3">Legacy ID</th>
                      <th className="py-1 pr-3">Giorno</th>
                      <th className="py-1 pr-3">Ore</th>
                      <th className="py-1 pr-3">Orario</th>
                      <th className="py-1 pr-3">Motivo perm.</th>
                      <th className="py-1 pr-3">Ore ass.</th>
                    </>
                  ) : (
                    <>
                      <th className="py-1 pr-3">Codice</th>
                      <th className="py-1 pr-3">Descrizione</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, idx) => {
                  if (mode === "dipendenti") {
                    const parts = (r.nome_completo || "").trim().split(/\s+/);
                    const cog = (parts[0] || "").toUpperCase();
                    const nom = parts.slice(1).join(" ").toUpperCase();
                    return (
                      <tr key={idx} className="border-t border-[var(--c-border-soft)]">
                        <td className="py-1 pr-3 text-[var(--c-text-muted)]">{idx + 1}</td>
                        <td className="py-1 pr-3">{r.legacy_id || "—"}</td>
                        <td className="py-1 pr-3 font-bold">{cog}</td>
                        <td className="py-1 pr-3">{nom}</td>
                      </tr>
                    );
                  }
                  if (mode === "motivi") {
                    return (
                      <tr key={idx} className="border-t border-[var(--c-border-soft)]">
                        <td className="py-1 pr-3 text-[var(--c-text-muted)]">{idx + 1}</td>
                        <td className="py-1 pr-3 font-bold uppercase">{r.codice}</td>
                        <td className="py-1 pr-3">{r.descrizione || "—"}</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={idx} className="border-t border-[var(--c-border-soft)]">
                      <td className="py-1 pr-3 text-[var(--c-text-muted)]">{idx + 1}</td>
                      <td className="py-1 pr-3 font-bold">{r.legacy_id}</td>
                      <td className="py-1 pr-3">{r.giorno}</td>
                      <td className="py-1 pr-3 tabular-nums">{r.ore}</td>
                      <td className="py-1 pr-3 text-[var(--c-text-muted)]">{r.orario || "—"}</td>
                      <td className={`py-1 pr-3 ${r.motivo_perm ? "text-[var(--c-accent)] font-bold" : "text-[var(--c-text-muted)]"}`}>{r.motivo_perm || "—"}</td>
                      <td className={`py-1 pr-3 tabular-nums ${r.ore_assenza > 0 ? "text-[var(--c-accent)] font-bold" : "text-[var(--c-text-muted)]"}`}>{r.ore_assenza || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          {mode === "dipendenti" && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={skipExisting}
                onChange={(e) => setSkipExisting(e.target.checked)}
                className="w-4 h-4 accent-[var(--c-accent)]"
                data-testid="import-skip-existing"
              />
              <span className="font-mono text-xs">
                Salta dipendenti già esistenti
              </span>
            </label>
          )}
          {mode === "profilo" && (
            <div className="font-mono text-[11px] text-[var(--c-text-muted)]">
              I profili esistenti vengono sovrascritti per i giorni presenti nelle righe.
            </div>
          )}
          {mode === "motivi" && (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">Modalità:</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="motivi-mode" checked={motiviMode === "merge"} onChange={() => setMotiviMode("merge")} data-testid="motivi-mode-merge" />
                <span className="font-mono text-xs">Aggiorna esistenti</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="motivi-mode" checked={motiviMode === "replace"} onChange={() => setMotiviMode("replace")} data-testid="motivi-mode-replace" />
                <span className="font-mono text-xs text-[var(--c-accent)]">Sostituisci tutto</span>
              </label>
            </div>
          )}
          <button
            onClick={runImport}
            disabled={busy || totalRows === 0}
            className="px-5 py-3 font-mono text-xs uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40"
            data-testid="import-run"
          >
            {busy ? "Importazione…" : `Importa ${totalRows || ""}`}
          </button>
        </div>
      </div>

      {result && result.kind === "motivi" && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-4 font-mono text-xs space-y-2" data-testid="import-result-motivi">
          <div className="text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">
            Esito (Motivi Assenza) — modalità {result.mode}
          </div>
          <div>Nuovi: <strong className="text-emerald-700">{result.created_count}</strong></div>
          <div>Aggiornati: <strong className="text-blue-700">{result.updated_count}</strong></div>
          <div>Saltati (codice vuoto): {result.skipped_count}</div>
          <div>Errori: <strong className={result.errors_count > 0 ? "text-red-700" : ""}>{result.errors_count}</strong></div>
        </div>
      )}

      {result && result.kind === "dipendenti" && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-4 font-mono text-xs space-y-2" data-testid="import-result">
          <div className="text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">Esito (Dipendenti)</div>
          <div>Creati: <strong className="text-emerald-700">{result.created_count}</strong></div>
          <div>Saltati: <strong className="text-amber-700">{result.skipped_count}</strong></div>
          <div>Errori: <strong className={result.errors_count > 0 ? "text-red-700" : ""}>{result.errors_count}</strong></div>
          {result.skipped && result.skipped.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[var(--c-text-muted)]">Dettaglio saltati</summary>
              <ul className="ml-4 mt-1 list-disc">
                {result.skipped.map((s, i) => (<li key={i}>{s.raw} — {s.reason}</li>))}
              </ul>
            </details>
          )}
          {result.errors && result.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-red-700">Dettaglio errori</summary>
              <ul className="ml-4 mt-1 list-disc">
                {result.errors.map((e, i) => (<li key={i}>{e.raw} — {e.error}</li>))}
              </ul>
            </details>
          )}
        </div>
      )}

      {result && result.kind === "profilo" && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border-soft)] p-4 font-mono text-xs space-y-2" data-testid="import-result-profilo">
          <div className="text-[10px] uppercase tracking-widest text-[var(--c-text-muted)]">Esito (Profili Orari)</div>
          <div>Righe ricevute: {result.rows_received}</div>
          <div>Dipendenti aggiornati: <strong className="text-emerald-700">{result.updated_count}</strong></div>
          <div>Giorno non valido: {result.bad_giorno_count}</div>
          <div>Legacy ID non trovati: <strong className={result.missing_legacy_ids.length > 0 ? "text-amber-700" : ""}>{result.missing_legacy_ids.length}</strong></div>
          {result.missing_legacy_ids.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-amber-700">Lista ID mancanti</summary>
              <div className="ml-4 mt-1 text-[var(--c-text-muted)]">{result.missing_legacy_ids.join(", ")}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

